import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Form, InputGroup, Spinner, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import PageHeader from 'components/common/PageHeader';
import axiosInstance from 'axiosInstance';

const SalesAccountsToActivatePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [savingAccountId, setSavingAccountId] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [inviteEmails, setInviteEmails] = useState({});

  useEffect(() => {
    let mounted = true;

    const loadAccounts = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await axiosInstance.get('sales/customers/accounts-to-activate/');
        if (!mounted) return;
        setAccounts(response.data?.accounts || []);
      } catch (requestError) {
        if (!mounted) return;
        setError(requestError?.response?.data?.error || t('SALES.ERROR_LOAD_ACCOUNTS'));
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadAccounts();
    return () => {
      mounted = false;
    };
  }, [t]);

  const updateAccount = async (accountId, payload, successMessage) => {
    setSavingAccountId(String(accountId));
    setError('');
    setSuccess('');
    try {
      const response = await axiosInstance.patch(`sales/customers/accounts/${accountId}/`, payload);
      const updated = response.data?.account;
      if (updated) {
        setAccounts(previous => previous.map(item => (item.id === updated.id ? updated : item)));
      }
      setSuccess(successMessage);
    } catch (requestError) {
      setError(requestError?.response?.data?.error || t('SALES.ERROR_UPDATE_ACCOUNT'));
    } finally {
      setSavingAccountId('');
    }
  };

  const inviteUser = async accountId => {
    const email = (inviteEmails[accountId] || '').trim();
    if (!email) {
      setError(t('SALES.ERROR_INVITE_EMAIL_REQUIRED'));
      return;
    }

    setSavingAccountId(String(accountId));
    setError('');
    setSuccess('');
    try {
      await axiosInstance.post(`sales/customers/accounts/${accountId}/invite/`, { email, role: 'admin' });
      setInviteEmails(previous => ({ ...previous, [accountId]: '' }));
      setSuccess(t('SALES.INVITE_SENT_SUCCESS'));
    } catch (requestError) {
      setError(requestError?.response?.data?.error || t('SALES.ERROR_INVITE_USER'));
    } finally {
      setSavingAccountId('');
    }
  };

  return (
    <>
      <PageHeader title={t('SALES.ACCOUNTS_TO_ACTIVATE')} className="mb-3" />

      <Card className="mb-3 border-primary-subtle">
        <Card.Body className="d-flex flex-wrap justify-content-between align-items-center gap-3">
          <div>
            <div className="text-primary fw-semi-bold">{t('SALES.ACTIVATION_QUEUE')}</div>
            <div className="text-600 fs--1">{t('SALES.ACTIVATION_QUEUE_HELP')}</div>
          </div>
          <Button size="sm" variant="outline-secondary" onClick={() => navigate('/sales/pipeline')}>
            {t('SALES.VIEW_WON_OPPORTUNITIES')}
          </Button>
        </Card.Body>
      </Card>

      {error ? <Alert variant="danger">{error}</Alert> : null}
      {success ? <Alert variant="success">{success}</Alert> : null}

      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center py-4"><Spinner animation="border" /></div>
          ) : (
            <Table responsive hover className="mb-0">
              <thead>
                <tr>
                  <th>{t('SALES.COMPANY')}</th>
                  <th>{t('SALES.BILLING_EMAIL')}</th>
                  <th>{t('SALES.ACCOUNT_STATUS')}</th>
                  <th>{t('SALES.ONBOARDING_STATUS')}</th>
                  <th className="text-end">{t('SALES.TABLE_ACTIONS')}</th>
                </tr>
              </thead>
              <tbody>
                {accounts.length ? accounts.map(item => (
                  <tr key={item.id}>
                    <td>
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0"
                        onClick={() => navigate(`/sales/company/${item.company_id}`)}
                      >
                        {item.company_name || item.company_id}
                      </Button>
                    </td>
                    <td>{item.billing_email || '-'}</td>
                    <td>
                      <Badge bg={item.status === 'active' ? 'success' : item.status === 'pending_activation' ? 'warning' : 'secondary'}>
                        {item.status}
                      </Badge>
                    </td>
                    <td>{item.onboarding_status || '-'}</td>
                    <td className="text-end">
                      <div className="d-flex flex-wrap gap-2 justify-content-end">
                        <Button
                          size="sm"
                          variant="outline-success"
                          disabled={savingAccountId === String(item.id) || item.status === 'active'}
                          onClick={() => updateAccount(
                            item.id,
                            { status: 'active', onboarding_status: item.onboarding_status || 'in_progress' },
                            t('SALES.ACCOUNT_ACTIVATED_SUCCESS')
                          )}
                        >
                          {t('SALES.ACTIVATE')}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          disabled={savingAccountId === String(item.id) || item.onboarding_status === 'in_progress'}
                          onClick={() => updateAccount(
                            item.id,
                            { onboarding_status: 'in_progress' },
                            t('SALES.ONBOARDING_STARTED_SUCCESS')
                          )}
                        >
                          {t('SALES.START_ONBOARDING')}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-dark"
                          disabled={savingAccountId === String(item.id) || item.onboarding_status === 'done'}
                          onClick={() => updateAccount(
                            item.id,
                            { onboarding_status: 'done' },
                            t('SALES.ONBOARDING_DONE_SUCCESS')
                          )}
                        >
                          {t('SALES.MARK_ONBOARDING_DONE')}
                        </Button>
                      </div>
                      <InputGroup className="mt-2" size="sm">
                        <Form.Control
                          type="email"
                          value={inviteEmails[item.id] || ''}
                          onChange={event => setInviteEmails(previous => ({ ...previous, [item.id]: event.target.value }))}
                          placeholder={t('SALES.INVITE_EMAIL_PLACEHOLDER')}
                        />
                        <Button
                          variant="outline-secondary"
                          disabled={savingAccountId === String(item.id)}
                          onClick={() => inviteUser(item.id)}
                        >
                          {t('SALES.SEND_INVITE')}
                        </Button>
                      </InputGroup>
                      {item.originating_opportunity_id ? (
                        <div className="mt-2 d-flex justify-content-end">
                          <Button
                            size="sm"
                            variant="link"
                            className="p-0"
                            onClick={() => navigate(`/sales/opportunity/${item.originating_opportunity_id}`)}
                          >
                            {t('SALES.BACK_TO_ORIGIN_DEAL')}: {item.originating_opportunity_title || `#${item.originating_opportunity_id}`}
                          </Button>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={5} className="text-center text-600 py-4">
                      <div className="mb-3">{t('SALES.EMPTY_ACCOUNTS_TABLE')}</div>
                      <Button size="sm" variant="outline-secondary" onClick={() => navigate('/sales/pipeline')}>
                        {t('SALES.VIEW_WON_OPPORTUNITIES')}
                      </Button>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </>
  );
};

export default SalesAccountsToActivatePage;
